/**
 * TabRouter — 최상위 탭 라우팅의 소유 객체 (Phaser 의존 0의 순수 코어).
 *
 * GameScene이 필드 3개(selection/enabledTabs/activeTab) + setActiveTab 메서드로 흩어 들고 있던
 * "탭 가시성 SSoT + 라우팅 결정"을 한 책임으로 응집한다. 순수 결정(빌드 활성 탭 필터·select 게이트·
 * visibility 파생)만 여기서 소유하고, Phaser 부수효과(모달 닫기/탭뷰 가시성/하단바 하이라이트/
 * 튜토리얼 통지/활성 탭 갱신)는 TabRouterEffects 콜백으로 위임한다 (Humble — 결정/효과 분리).
 *
 * activeTab은 별도 필드가 아니라 selection.active다 (select 성공 시 항상 일치). 저장계는
 * current()로 단일값을 읽으므로 progress 묶음과 섞이지 않는다 (저장 경계 훼손 0).
 */
import { SingleSelection } from "@yjsgames/single-select";
import type { FeatureFlags } from "../env/featureFlags.js";
import {
  TAB_ORDER,
  deriveEnabledTabSet,
  filterTabOrder,
  type TabId,
} from "../ui/tabVisibility.js";

/**
 * 탭 전환에 수반되는 Phaser 부수효과 — TabRouter는 결정만 하고 효과는 전부 이 콜백에 위임한다.
 * setActive 성공(활성 탭) 시에만 아래 순서로 호출된다: closeOverlays → applyVisibility → setBottomActive
 * → onTabEntered → notifyAction → onActivated. 비활성 빌드 탭은 select 게이트에서 막혀 어떤 효과도 호출되지 않는다.
 *
 * ⚠ onTabEntered는 notifyAction 바로 앞이어야 한다 — 탭 진입 튜토리얼 시작(startScenario)이 띄운
 * 첫 스텝(advance=action, actionKey="tab.<TabId>")을 곧바로 오는 notifyAction이 소비해 본문 스텝으로
 * 진행시킨다. onTabEntered를 notifyAction 뒤(또는 onActivated)에 두면 그 첫 스텝이 무한 대기한다.
 */
export interface TabRouterEffects {
  /** 열린 오버레이/모달 닫기 (탭 전환 시 정리). */
  closeOverlays(): void;
  /** 탭 콘텐츠 가시성 일괄 적용 (정확히 하나만 true). */
  applyVisibility(vis: Record<TabId, boolean>): void;
  /** 하단 탭바 활성 하이라이트. */
  setBottomActive(tab: TabId): void;
  /** 탭 진입 통지 — 진입 튜토리얼 자동 시작 훅 (notifyAction 바로 앞 — 위 ⚠ 참조). */
  onTabEntered(tab: TabId): void;
  /** 튜토리얼 action 스텝 통지 (prefix 부착은 콜백 측 — TabRouter는 raw TabId만 전달). */
  notifyAction(tab: TabId): void;
  /** 활성 탭 갱신 트리거 (refreshActiveTab — readiness 게이트는 콜백 측). */
  onActivated(): void;
  /** 챕터 잠금 탭 클릭 거부 — 흔들림+토스트(requiredChapter=해제에 필요한 챕터). 선택은 일어나지 않는다. */
  onTabLocked(tab: TabId, requiredChapter: number): void;
}

/**
 * 챕터 잠금 판정 — 탭이 잠겼으면 해제에 필요한 챕터 수를, 해제 상태면 null을 반환한다(lazy 평가).
 * GameScene이 현재 bestChapter를 읽어 tabLock 순수 함수로 구현해 주입한다(부팅 초기 미확정 회피).
 */
export type TabLockResolver = (tab: TabId) => number | null;

export class TabRouter {
  private readonly selection: SingleSelection<TabId>;
  /** 빌드 활성 탭 집합 (features 파생) — ShellFrame/탭바 구성원 필터에 노출. */
  readonly enabled: ReadonlySet<TabId>;

  constructor(
    enabled: ReadonlySet<TabId>,
    private readonly effects: TabRouterEffects,
    private readonly lockResolver: TabLockResolver = () => null,
  ) {
    this.enabled = enabled;
    this.selection = new SingleSelection<TabId>(
      [...filterTabOrder(TAB_ORDER, enabled)],
      "battle",
    );
  }

  /** 현재 활성 탭. activeTab의 SSoT (별도 필드 없음). */
  current(): TabId {
    return this.selection.active;
  }

  /** 전투 탭 활성 여부 — battle은 250ms 주기 재구성을 건너뛴다(드래그 보존). */
  isBattle(): boolean {
    return this.selection.active === "battle";
  }

  /**
   * 탭 전환. 비활성 빌드 탭(selection 비구성원)은 select가 false를 반환해 no-op(효과 미호출 — 외부 호출 방어).
   * 활성 탭이면 가시성 파생 후 effects를 정해진 순서로 호출한다.
   */
  setActive(tab: TabId): void {
    // 챕터 잠금 게이트 — select보다 먼저. 잠긴 탭은 선택하지 않고(current 불변) 거부 피드백만 낸다.
    const requiredChapter = this.lockResolver(tab);
    if (requiredChapter != null) {
      this.effects.onTabLocked(tab, requiredChapter);
      return;
    }
    if (!this.selection.select(tab)) return;
    this.effects.closeOverlays();
    this.effects.applyVisibility(this.selection.visibility());
    this.effects.setBottomActive(tab);
    // ⚠ onTabEntered는 notifyAction 바로 앞 — 진입 튜토리얼 첫 스텝(action tab.<TabId>)을
    // 곧바로 오는 notifyAction이 소비해 본문 스텝으로 진행시킨다 (뒤에 두면 무한 대기).
    this.effects.onTabEntered(tab);
    this.effects.notifyAction(tab);
    this.effects.onActivated();
  }

  /**
   * 저장 로드 시 활성 탭을 battle로 초기화 (저장된 activeTab은 항상 battle로 환원 — 기존 동작).
   * buildUi 후 setActive가 가시성을 확정하므로 여기서는 effects를 호출하지 않는다.
   */
  resetToBattle(): void {
    this.selection.select("battle");
  }
}

/** features → enabled 집합 도출 후 TabRouter 생성 (GameScene create의 selection 구성 블록 흡수). */
export function createTabRouter(
  features: FeatureFlags,
  effects: TabRouterEffects,
  lockResolver?: TabLockResolver,
): TabRouter {
  return new TabRouter(deriveEnabledTabSet(features), effects, lockResolver);
}
